import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useDialog } from "@/context/DialogProvider";
import {
  FileText, Plus, Trash2, Pencil, X as XIcon, Upload,
  BadgeCheck, Bug, CheckCircle2, XCircle,
} from "lucide-react";
import StepLayout from "./StepLayout";

// Mirrors the canonical category list from Documentation.jsx so the
// per-category document requirements line up.
const VENDOR_CATEGORIES = [
  "Pest Control",
  "Produce", "Meat / Seafood", "Dairy", "Dry Goods",
  "Packaging", "Chemicals / Sanitisers", "Calibration",
  "Transport / Logistics", "Other",
];
const FOOD_CATEGORIES = ["Produce", "Meat / Seafood", "Dairy", "Dry Goods"];

// Mirrors PERF_CRITERIA in the live Documentation page (same keys, same
// labels) so wizard-entered scores hydrate the live evaluation modal
// identically.
const PERF_CRITERIA = [
  { key: "customer_service",   label: "Quality of Customer Service" },
  { key: "delivery_adherence", label: "Adherence to Delivery Instruction / Requests" },
  { key: "lead_time",          label: "Lead time required to perform task" },
  { key: "quality_safety",     label: "Quality & Food Safety / Competency of personnel" },
  { key: "pricing",            label: "Competitive Pricing" },
];

const SCORE_LEGEND = [
  { n: 5, label: "Excellent" },
  { n: 4, label: "Very Good" },
  { n: 3, label: "Satisfy" },
  { n: 2, label: "Poor" },
  { n: 1, label: "Bad" },
];

/**
 * Per-category document requirement matrix — mirrors the DocumentSlot
 * config in the live VendorRow (Documentation.jsx). Owners can upload
 * one file per `requirementKind` directly during onboarding so the
 * matching slot in the live Approved-Vendor view is pre-populated.
 */
function docSlotsForCategory(category) {
  const isPest = category === "Pest Control";
  const isFood = FOOD_CATEGORIES.includes(category);
  const slots = [
    { kind: "vendor_evaluation_form", label: "Evaluation declaration form (F01a)",
      hint: "Signed by the supplier", required: true },
  ];
  if (isPest) {
    slots.push({ kind: "vendor_pest_contract", label: "Pest Control Contract",
      hint: "Service agreement; track end date", required: true });
  }
  if (isPest || isFood) {
    slots.push({
      kind: "vendor_license",
      label: isPest ? "Company License & Technician License" : "Vendor license / accreditation",
      hint: isPest ? "Upload both licenses; track validity" : "Track licence validity",
      required: true,
    });
  }
  if (isPest) {
    slots.push({ kind: "vendor_msds", label: "SDS of Pest Control Chemicals",
      hint: "Update when chemicals change", required: true });
  }
  slots.push({ kind: "vendor_supplementary", label: "Supplementary documents (optional)",
    hint: "Anything else you'd like to keep on file", required: false });
  return slots;
}

/**
 * Step 7 — Documentation.
 *
 * Owners can scaffold their Approved Vendor list and Pest-Control supplier
 * roster during onboarding. The full Documentation module on the live app
 * captures full vendor profiles + uploaded documents + performance
 * scoring — but the wizard intentionally keeps it light:
 *
 *   1. Add vendors with name + category + contact (~10 seconds each).
 *   2. Tick "I have full details now" on any vendor to expand the form
 *      and capture contact email / phone, performance scores (5 × 5 pt)
 *      and a single document upload right inside the wizard.
 *   3. Step is optional — owners can skip and complete from the live
 *      Documentation page later. Skipping still advances the wizard.
 */
export default function Step8Documentation({
  stepIndex, totalSteps, onAdvance, onBack, onFinishLater,
}) {
  const { notify } = useDialog();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Quick-add form state. `expanded=true` reveals the full-detail block.
  const [draft, setDraft] = useState(blankDraft());
  const [expanded, setExpanded] = useState(false);
  // Edit modal state
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);

  function blankDraft() {
    return {
      name: "",
      category: VENDOR_CATEGORIES[0],
      contact_person: "",
      contact_number: "",
      contact_email: "",
      product_service: "",
      has_full_details: false,
      // Default every score to 3 (Satisfy) — same default as the live
      // evaluation modal so total starts at 15 (the delisting threshold).
      performance_scores: PERF_CRITERIA.reduce((a, c) => ({ ...a, [c.key]: 3 }), {}),
      performance_notes: "",
      // { [requirementKind]: File } — populated when owner picks files in
      // the expanded "I have full details now" panel.
      _files: {},
    };
  }

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/vendors");
      setVendors(data.vendors || []);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Couldn't load vendors");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const addVendor = async () => {
    if (!draft.name.trim()) {
      return notify({ title: "Vendor name required", message: "Give the vendor a name.", variant: "err" });
    }
    setBusy(true); setErr("");
    try {
      const payload = {
        name: draft.name.trim(),
        category: draft.category,
        contact_person: draft.contact_person.trim(),
        contact_number: draft.contact_number.trim(),
        contact_email: draft.contact_email.trim(),
        product_service: draft.product_service.trim(),
        site_assignments: "all",
        evaluation_outcome: null,
        notes: "",
      };
      const { data } = await api.post("/vendors", payload);
      // Performance score → uses the dedicated endpoint so the audit-log
      // entry + delist gate fire identically to the live Documentation page.
      if (draft.has_full_details) {
        const total = PERF_CRITERIA.reduce((s, c) => s + (Number(draft.performance_scores[c.key]) || 0), 0);
        try {
          await api.post(`/vendors/${data.id}/performance`, {
            ...draft.performance_scores,
            evaluation_date: new Date().toISOString().slice(0, 10),
            notes: draft.performance_notes,
          });
          if (total < 15) {
            await notify({
              title: "Vendor saved — auto-delisted",
              message: `Total ${total}/25 is below the 15-point threshold. Re-evaluate later from the Documentation page.`,
              variant: "warn",
            });
          }
        } catch (perfErr) {
          await notify({
            title: "Vendor saved, performance save failed",
            message: formatErr(perfErr.response?.data?.detail) || "Re-try from the live page.",
            variant: "warn",
          });
        }
      }
      // Per-category document uploads — one per requirementKind.
      const fileEntries = Object.entries(draft._files || {}).filter(([, f]) => f);
      for (const [kind, file] of fileEntries) {
        const fd = new FormData();
        fd.append("owner_type", "vendor");
        fd.append("owner_id", data.id);
        fd.append("requirement_kind", kind);
        fd.append("file", file);
        try {
          await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (uploadErr) {
          await notify({
            title: `Couldn't upload ${kind.replace("vendor_", "").replace(/_/g, " ")}`,
            message: formatErr(uploadErr.response?.data?.detail) || "Try again from the Documentation page.",
            variant: "warn",
          });
        }
      }
      setDraft(blankDraft());
      setExpanded(false);
      await load();
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || "Couldn't add vendor");
    } finally { setBusy(false); }
  };

  const removeVendor = async (vid) => {
    setBusy(true);
    try { await api.delete(`/vendors/${vid}`); await load(); }
    catch (ex) {
      await notify({
        title: "Couldn't remove vendor",
        message: formatErr(ex.response?.data?.detail) || "Please try again.",
        variant: "err",
      });
    }
    finally { setBusy(false); }
  };

  const openEdit = (v) => {
    setEditingId(v.id);
    setEditDraft({
      name: v.name || "",
      category: v.category || VENDOR_CATEGORIES[0],
      contact_person: v.contact_person || "",
      contact_number: v.contact_number || "",
      contact_email: v.contact_email || "",
      product_service: v.product_service || "",
      performance_scores: PERF_CRITERIA.reduce(
        (a, c) => ({ ...a, [c.key]: Number(v.performance_scores?.[c.key]) || 3 }), {},
      ),
      performance_notes: "",
      _has_perf: !!v.performance_scores,
      _site_assignments: v.site_assignments,
      _evaluation_outcome: v.evaluation_outcome,
    });
  };
  const closeEdit = () => { setEditingId(null); setEditDraft(null); };
  const saveEdit = async () => {
    if (!editDraft.name.trim()) {
      return notify({ title: "Name required", message: "Vendor name can't be empty.", variant: "err" });
    }
    setBusy(true);
    try {
      await api.patch(`/vendors/${editingId}`, {
        name: editDraft.name.trim(),
        category: editDraft.category,
        contact_person: editDraft.contact_person.trim(),
        contact_number: editDraft.contact_number.trim(),
        contact_email: editDraft.contact_email.trim(),
        product_service: editDraft.product_service.trim(),
        site_assignments: editDraft._site_assignments || "all",
        evaluation_outcome: editDraft._evaluation_outcome,
        notes: "",
      });
      if (editDraft._has_perf) {
        await api.post(`/vendors/${editingId}/performance`, {
          ...editDraft.performance_scores,
          evaluation_date: new Date().toISOString().slice(0, 10),
          notes: editDraft.performance_notes || "",
        });
      }
      closeEdit();
      await load();
    } catch (ex) {
      await notify({
        title: "Couldn't save",
        message: formatErr(ex.response?.data?.detail) || "Please try again.",
        variant: "err",
      });
    } finally { setBusy(false); }
  };

  const totalScore = (scores) => Object.values(scores || {}).reduce((a, b) => a + (parseInt(b, 10) || 0), 0);
  // Reference to keep the helper available for the row badge display.
  void totalScore;

  return (
    <StepLayout
      stepIndex={stepIndex} totalSteps={totalSteps}
      title="Approved vendors & pest control"
      subtitle="Captures your supplier roster — food, cleaning chemicals, pest control, equipment maintenance."
      description={
        <ul className="text-xs opacity-80 list-disc pl-4 leading-relaxed space-y-1">
          <li>Add vendors quickly with just a name + category + contact.</li>
          <li>Tick <b>I have full details now</b> to capture contact info, performance scores and upload an evaluation certificate inline.</li>
          <li>Pest-control suppliers entered here can be linked to a site from the Documentation page later.</li>
          <li>This step is <b>optional</b> — you can finish it after onboarding.</li>
        </ul>
      }
      illustration={
        <div className="vb-card p-4 text-xs space-y-1.5">
          <div className="flex items-center gap-2"><FileText size={14} style={{ color: "var(--vb-accent)" }} /> <b>Vendor master list</b> (ISO 22000 PRP-10).</div>
          <div className="flex items-center gap-2"><Bug size={14} style={{ color: "var(--vb-accent)" }} /> <b>Pest control</b> — assign a supplier per site.</div>
          <div className="flex items-center gap-2"><BadgeCheck size={14} style={{ color: "var(--vb-accent)" }} /> <b>Performance scoring</b> — 5 criteria, 1-5 each. &lt;15 auto-delists.</div>
        </div>
      }
      errorMsg={err}
      onBack={onBack}
      onFinishLater={onFinishLater}
      onNext={() => onAdvance({ documentation: true })}
      nextLabel={vendors.length > 0 ? "Save & continue" : "Skip — set up later"}
      busy={busy || loading}
    >
      {/* Quick-add card */}
      <div className="vb-card p-5 mb-6" data-testid="wiz-doc-add-card">
        <h2 className="vb-heading text-lg mb-3" style={{ color: "var(--vb-text-2)" }}>Add a vendor</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Vendor name *</Label>
            <input className="vb-input" value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="e.g. Sunshine Poultry Pte Ltd"
              data-testid="wiz-doc-name" />
          </div>
          <div>
            <Label>Category</Label>
            <select className="vb-input" value={draft.category}
              onChange={(e) => setDraft({ ...draft, category: e.target.value })}
              data-testid="wiz-doc-category">
              {VENDOR_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <Label>Contact person</Label>
            <input className="vb-input" value={draft.contact_person}
              onChange={(e) => setDraft({ ...draft, contact_person: e.target.value })}
              data-testid="wiz-doc-contact-person" />
          </div>
          <div>
            <Label>Contact phone</Label>
            <input className="vb-input" value={draft.contact_number}
              onChange={(e) => setDraft({ ...draft, contact_number: e.target.value })}
              data-testid="wiz-doc-contact-number" />
          </div>
        </div>
        <label className="flex items-start gap-2 cursor-pointer mt-4">
          <input type="checkbox" className="mt-1"
            checked={expanded}
            onChange={(e) => { setExpanded(e.target.checked); setDraft({ ...draft, has_full_details: e.target.checked }); }}
            data-testid="wiz-doc-expand-cb" />
          <div>
            <div className="text-sm font-semibold" style={{ color: "var(--vb-text-2)" }}>I have full details now</div>
            <div className="text-[11px] opacity-70">Capture email, product/service, performance score and upload one document.</div>
          </div>
        </label>
        {expanded && (
          <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--vb-border)" }}
            data-testid="wiz-doc-expanded">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Contact email</Label>
                <input type="email" className="vb-input" value={draft.contact_email}
                  onChange={(e) => setDraft({ ...draft, contact_email: e.target.value })}
                  data-testid="wiz-doc-email" />
              </div>
              <div>
                <Label>Product / service</Label>
                <input className="vb-input" value={draft.product_service}
                  onChange={(e) => setDraft({ ...draft, product_service: e.target.value })}
                  data-testid="wiz-doc-product" />
              </div>
            </div>

            {/* Evaluation of Performance — same visual contract as the live
                VendorPerformanceModal in Documentation.jsx (scoring legend,
                1-5 round buttons, Total/25 banner with in-good-standing
                or delisted pill). */}
            <div className="mt-5">
              <div className="text-sm font-semibold mb-1" style={{ color: "var(--vb-text-2)" }}>
                Evaluation of Performance
              </div>
              <p className="text-[12px] opacity-70 m-0 mb-3">
                Score each criterion from <strong>1 (poor)</strong> to <strong>5 (excellent)</strong>.
                Total is out of 25. A vendor scoring <strong>below 15</strong> is automatically delisted.
              </p>
              <div className="vb-card p-3 mb-3 text-[12px]" data-testid="wiz-doc-perf-legend"
                style={{ background: "var(--vb-bg-2,#faf6f1)" }}>
                <div className="font-semibold mb-1" style={{ color: "var(--vb-text-2)" }}>Scoring legend</div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-1">
                  {SCORE_LEGEND.map((s) => (
                    <div key={s.n} className="flex items-center gap-2">
                      <span style={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        width: 22, height: 22, borderRadius: 11, fontWeight: 700,
                        background: "var(--vb-accent)", color: "white", fontSize: 11,
                      }}>{s.n}</span>
                      <span>{s.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-3">
                {PERF_CRITERIA.map((c) => (
                  <div key={c.key} className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-[13px] flex-1 min-w-[200px]">{c.label}</div>
                    <div className="flex items-center gap-1" data-testid={`wiz-doc-perf-row-${c.key}`}>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button key={n} type="button"
                          onClick={() => setDraft({
                            ...draft,
                            performance_scores: { ...draft.performance_scores, [c.key]: n },
                          })}
                          data-testid={`wiz-doc-perf-${c.key}-${n}`}
                          className="vb-btn sm"
                          style={{
                            minWidth: 32, padding: "4px 8px",
                            background: draft.performance_scores[c.key] === n ? "var(--vb-accent)" : "transparent",
                            color: draft.performance_scores[c.key] === n ? "white" : "var(--vb-text-2)",
                            border: "1px solid var(--vb-border)",
                          }}>
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {(() => {
                const total = PERF_CRITERIA.reduce((s, c) => s + (Number(draft.performance_scores[c.key]) || 0), 0);
                const delisted = total < 15;
                return (
                  <div className="flex items-center justify-between gap-3 mt-4 p-3 rounded" style={{
                    background: delisted ? "rgba(239,68,68,0.10)" : "rgba(34,197,94,0.10)",
                    border: `1px solid ${delisted ? "rgba(239,68,68,0.4)" : "rgba(34,197,94,0.4)"}`,
                  }}>
                    <div className="text-[13px]">
                      <strong>Total:&nbsp;</strong>
                      <span data-testid="wiz-doc-perf-total">{total} / 25</span>
                    </div>
                    {delisted
                      ? <span className="vb-pill err"><XCircle size={11} /> Delisted (&lt; 15)</span>
                      : <span className="vb-pill ok"><CheckCircle2 size={11} /> In good standing</span>}
                  </div>
                );
              })()}
              <div className="mt-3">
                <Label>Notes (optional)</Label>
                <input className="vb-input" value={draft.performance_notes}
                  onChange={(e) => setDraft({ ...draft, performance_notes: e.target.value })}
                  placeholder="Evidence, follow-up actions…"
                  data-testid="wiz-doc-perf-notes" />
              </div>
            </div>

            {/* Per-category document slots — mirrors DocumentSlot list in
                VendorRow's expanded view. One file per requirementKind;
                supplementary slot is always present and optional. */}
            <div className="mt-5">
              <div className="text-sm font-semibold mb-1" style={{ color: "var(--vb-text-2)" }}>
                Documents
              </div>
              <p className="text-[12px] opacity-70 m-0 mb-3">
                Requirements update based on the selected category. Optional now — you can upload later from the Documentation page.
              </p>
              <div className="space-y-2">
                {docSlotsForCategory(draft.category).map((slot) => (
                  <div key={slot.kind} className="vb-card p-3 flex items-center gap-3"
                    data-testid={`wiz-doc-slot-${slot.kind}`}>
                    <FileText size={14} style={{ color: "var(--vb-accent)" }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium" style={{ color: "var(--vb-text-2)" }}>
                        {slot.label}
                        {slot.required && <span style={{ color: "var(--vb-error)" }}> *</span>}
                      </div>
                      <div className="text-[11px] opacity-60">{slot.hint}</div>
                      {draft._files?.[slot.kind] && (
                        <div className="text-[11px] mt-0.5" style={{ color: "var(--vb-accent)" }}>
                          📎 {draft._files[slot.kind].name}
                        </div>
                      )}
                    </div>
                    <label className="vb-btn ghost sm cursor-pointer"
                      data-testid={`wiz-doc-slot-${slot.kind}-pick`}>
                      <Upload size={12} /> {draft._files?.[slot.kind] ? "Replace" : "Choose file"}
                      <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden"
                        onChange={(e) => setDraft({
                          ...draft,
                          _files: { ...draft._files, [slot.kind]: e.target.files?.[0] || null },
                        })} />
                    </label>
                    {draft._files?.[slot.kind] && (
                      <button type="button" className="vb-btn ghost sm"
                        onClick={() => {
                          const next = { ...(draft._files || {}) };
                          delete next[slot.kind];
                          setDraft({ ...draft, _files: next });
                        }}
                        style={{ color: "var(--vb-error)" }}
                        data-testid={`wiz-doc-slot-${slot.kind}-clear`}>
                        <XIcon size={12} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        <button type="button" className="vb-btn mt-4" onClick={addVendor} disabled={busy}
          data-testid="wiz-doc-add-btn">
          <Plus size={14} /> Add vendor
        </button>
      </div>

      <h2 className="vb-heading text-base mb-2" style={{ color: "var(--vb-text-2)" }}>
        Vendors added ({vendors.length})
      </h2>
      {vendors.length === 0 ? (
        <div className="vb-card p-6 text-center text-sm opacity-60" data-testid="wiz-doc-empty">
          No vendors yet — add a few above, or skip and set them up later.
        </div>
      ) : (
        <div className="vb-card p-0 overflow-hidden" data-testid="wiz-doc-list">
          <table className="vb-table">
            <thead><tr><th>Vendor</th><th>Category</th><th>Contact</th><th>Score</th><th></th></tr></thead>
            <tbody>
              {vendors.map((v) => {
                const ts = totalScore(v.performance_scores);
                return (
                  <tr key={v.id} data-testid={`wiz-doc-row-${v.id}`}>
                    <td>
                      <strong>{v.name}</strong>
                      {v.product_service && <div className="text-xs opacity-60">{v.product_service}</div>}
                    </td>
                    <td className="text-xs">{v.category}</td>
                    <td className="text-xs">
                      {v.contact_person && <div>{v.contact_person}</div>}
                      {v.contact_number && <div className="opacity-60">{v.contact_number}</div>}
                    </td>
                    <td className="text-xs">
                      {v.performance_scores ? (
                        <span className={`vb-pill text-[10px] ${ts < 15 ? "err" : "ok"}`}>
                          <BadgeCheck size={9} /> {ts}/25
                        </span>
                      ) : <span className="opacity-50">—</span>}
                    </td>
                    <td className="text-right whitespace-nowrap">
                      <button type="button" className="vb-btn ghost sm mr-1" onClick={() => openEdit(v)}
                        data-testid={`wiz-doc-edit-${v.id}`}>
                        <Pencil size={11} />
                      </button>
                      <button type="button" className="vb-btn ghost sm" onClick={() => removeVendor(v.id)}
                        style={{ color: "var(--vb-error)" }}
                        data-testid={`wiz-doc-del-${v.id}`}>
                        <Trash2 size={11} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editingId && editDraft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(60, 40, 24, 0.6)" }}
          data-testid="wiz-doc-edit-modal">
          <div className="vb-card max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h2 className="vb-heading text-lg m-0" style={{ color: "var(--vb-text-2)" }}>Edit vendor</h2>
              <button onClick={closeEdit} className="opacity-60 hover:opacity-100"
                data-testid="wiz-doc-edit-close"><XIcon size={18} /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label>Name *</Label>
                <input className="vb-input" value={editDraft.name}
                  onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                  data-testid="wiz-doc-edit-name" />
              </div>
              <div>
                <Label>Category</Label>
                <select className="vb-input" value={editDraft.category}
                  onChange={(e) => setEditDraft({ ...editDraft, category: e.target.value })}
                  data-testid="wiz-doc-edit-category">
                  {VENDOR_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label>Product / service</Label>
                <input className="vb-input" value={editDraft.product_service}
                  onChange={(e) => setEditDraft({ ...editDraft, product_service: e.target.value })} />
              </div>
              <div>
                <Label>Contact person</Label>
                <input className="vb-input" value={editDraft.contact_person}
                  onChange={(e) => setEditDraft({ ...editDraft, contact_person: e.target.value })} />
              </div>
              <div>
                <Label>Contact phone</Label>
                <input className="vb-input" value={editDraft.contact_number}
                  onChange={(e) => setEditDraft({ ...editDraft, contact_number: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <Label>Contact email</Label>
                <input type="email" className="vb-input" value={editDraft.contact_email}
                  onChange={(e) => setEditDraft({ ...editDraft, contact_email: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={editDraft._has_perf}
                    onChange={(e) => setEditDraft({ ...editDraft, _has_perf: e.target.checked })}
                    data-testid="wiz-doc-edit-has-perf" />
                  <span className="text-sm">Record performance score</span>
                </label>
                {editDraft._has_perf && (
                  <div className="mt-3">
                    <div className="vb-card p-3 mb-3 text-[12px]" style={{ background: "var(--vb-bg-2,#faf6f1)" }}>
                      <div className="font-semibold mb-1" style={{ color: "var(--vb-text-2)" }}>Scoring legend</div>
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-1">
                        {SCORE_LEGEND.map((s) => (
                          <div key={s.n} className="flex items-center gap-2">
                            <span style={{
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                              width: 22, height: 22, borderRadius: 11, fontWeight: 700,
                              background: "var(--vb-accent)", color: "white", fontSize: 11,
                            }}>{s.n}</span>
                            <span>{s.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-3">
                      {PERF_CRITERIA.map((c) => (
                        <div key={c.key} className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="text-[13px] flex-1 min-w-[200px]">{c.label}</div>
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((n) => (
                              <button key={n} type="button"
                                onClick={() => setEditDraft({
                                  ...editDraft,
                                  performance_scores: { ...editDraft.performance_scores, [c.key]: n },
                                })}
                                data-testid={`wiz-doc-edit-perf-${c.key}-${n}`}
                                className="vb-btn sm"
                                style={{
                                  minWidth: 32, padding: "4px 8px",
                                  background: editDraft.performance_scores[c.key] === n ? "var(--vb-accent)" : "transparent",
                                  color: editDraft.performance_scores[c.key] === n ? "white" : "var(--vb-text-2)",
                                  border: "1px solid var(--vb-border)",
                                }}>
                                {n}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    {(() => {
                      const total = PERF_CRITERIA.reduce((s, c) => s + (Number(editDraft.performance_scores[c.key]) || 0), 0);
                      const delisted = total < 15;
                      return (
                        <div className="flex items-center justify-between gap-3 mt-3 p-3 rounded" style={{
                          background: delisted ? "rgba(239,68,68,0.10)" : "rgba(34,197,94,0.10)",
                          border: `1px solid ${delisted ? "rgba(239,68,68,0.4)" : "rgba(34,197,94,0.4)"}`,
                        }}>
                          <div className="text-[13px]">
                            <strong>Total:&nbsp;</strong>
                            <span>{total} / 25</span>
                          </div>
                          {delisted
                            ? <span className="vb-pill err"><XCircle size={11} /> Delisted (&lt; 15)</span>
                            : <span className="vb-pill ok"><CheckCircle2 size={11} /> In good standing</span>}
                        </div>
                      );
                    })()}
                    <div className="mt-3">
                      <Label>Notes (optional)</Label>
                      <input className="vb-input" value={editDraft.performance_notes}
                        onChange={(e) => setEditDraft({ ...editDraft, performance_notes: e.target.value })}
                        placeholder="Evidence, follow-up actions…" />
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" className="vb-btn ghost" onClick={closeEdit} disabled={busy}
                data-testid="wiz-doc-edit-cancel">Cancel</button>
              <button type="button" className="vb-btn" onClick={saveEdit} disabled={busy}
                data-testid="wiz-doc-edit-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </StepLayout>
  );
}

function Label({ children }) {
  return (
    <label className="block text-xs font-semibold mb-1 uppercase tracking-wider"
      style={{ color: "var(--vb-text-2)" }}>{children}</label>
  );
}
